import React, { useState, useEffect, useCallback } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Button, Rate, Typography, Space, Card, Checkbox, Input, message } from 'antd';
import { SecurityScanOutlined, StarOutlined, CheckCircleOutlined } from '@ant-design/icons';
import './leave.scss';
import Header from '../../component/header/header';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface LeaveProps extends RouteComponentProps<{ slotBookingId: string; userId: string }> {}

interface FeedbackData {
  data: {
    star?: number;
  };
}

const Leave: React.FunctionComponent<LeaveProps> = ({ match, history }) => {
  const [countdown, setCountdown] = useState(2); // 15 seconds countdown
  const [rating, setRating] = useState(0);
  const [showRating, setShowRating] = useState(false);
  const [showDetailedFeedback, setShowDetailedFeedback] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [feedbackText, setFeedbackText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingFeedback, setExistingFeedback] = useState<FeedbackData | null>(null);
  const { slotBookingId, userId } = match.params;

  // Get host status from localStorage (saved before leaving meeting)
  const getIsHost = useCallback(() => {
    try {
      const savedIsHost = localStorage.getItem('isHost');
      return savedIsHost ? JSON.parse(savedIsHost) : false;
    } catch (error) {
      console.error('Error reading isHost from localStorage:', error);
      return false;
    }
  }, []);

  const isHost = getIsHost();

  // Check existing feedback on component mount
  useEffect(() => {
    // Don't show rating for host
    if (isHost) {
      return;
    }

    const checkExistingFeedback = async () => {
      try {
        const admin_url = process.env.REACT_APP_ADMIN_URL;
        const response = await fetch(`${admin_url}/domain/slotbookings/${slotBookingId}`);
        if (response.ok) {
          const data: FeedbackData = await response.json();
          setExistingFeedback(data);
          if (!data.data.star) {
            setShowRating(true);
          }
        } else {
          // If API call fails, show rating form
          setShowRating(true);
        }
      } catch (error) {
        console.error('Error checking existing feedback:', error);
        // If API call fails, show rating form
        setShowRating(true);
      }
    };

    checkExistingFeedback();
  }, [slotBookingId, isHost]);

  // Cleanup localStorage on component unmount
  // useEffect(() => {
  //   return () => {
  //     localStorage.removeItem('isHost');
  //   };
  // }, []);

  // Countdown timer
  // useEffect(() => {
  //   const timer = setInterval(() => {
  //     setCountdown((prev) => {
  //       if (prev <= 1) {
  //         clearInterval(timer);
  //         history.push('/');
  //         return 0;
  //       }
  //       return prev - 1;
  //     });
  //   }, 1000);

  //   return () => clearInterval(timer);
  // }, [history]);

  const handleRejoin = useCallback(() => {
    history.push(`/${slotBookingId}/${userId}`);
  }, [history, slotBookingId, userId]);

  const handleReturnHome = useCallback(() => {
    history.push('/');
  }, [history]);

  const handleRatingChange = useCallback((value: number) => {
    setRating(value);
    setShowDetailedFeedback(true);
    setSelectedOptions([]); // Reset selected options when rating changes
  }, []);

  const handleOptionChange = useCallback((checkedValues: any[]) => {
    setSelectedOptions(checkedValues as string[]);
  }, []);

  const handleSubmitFeedback = useCallback(async () => {
    if (selectedOptions.length === 0) {
      message.warning('Please select at least one option');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        whatWrongs: selectedOptions,
        star: rating,
        slotBookingSid: slotBookingId,
        feedback: feedbackText
      };
      const admin_url = process.env.REACT_APP_ADMIN_URL;
      const response = await fetch(`${admin_url}/domain/session_feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        message.success('Feedback submitted successfully!');
        setShowRating(false);
        setShowDetailedFeedback(false);
        setShowThankYou(true);
      } else {
        throw new Error('Failed to submit feedback');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      message.error('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedOptions, rating, slotBookingId, feedbackText]);

  // Get feedback options based on rating
  const getFeedbackOptions = useCallback(() => {
    if (rating >= 4) {
      return {
        title: 'What you liked?',
        options: ['Teacher', 'Platform', 'Overall']
      };
    } else {
      return {
        title: 'What could be made better?',
        options: ['Audio', 'Video', 'Presentation', 'Teacher', 'Internet', 'Language']
      };
    }
  }, [rating]);

  return (
    <div className="leave-container">
      {/* <div className="countdown-indicator">
          <div className="countdown-circle">
            <span className="countdown-number">{countdown}</span>
          </div>
          <Text className="countdown-text">Returning to home screen</Text>
        </div> */}

      <div className="leave-content">
        <Title level={1} className="leave-title">
          You left the meeting
        </Title>

        <Space size="large" className="action-buttons">
          <Button size="large" className="rejoin-button" onClick={handleRejoin}>
            Rejoin
          </Button>
          {(!showRating || isHost) && (
            <Button type="primary" size="large" className="home-button" onClick={handleReturnHome}>
              Return to home screen
            </Button>
          )}
        </Space>

        {showRating && !isHost && (
          <Card className="rating-card">
            <div className="rating-section">
              <Text className="rating-title">How was the audio and video?</Text>
              <div className="rating-stars">
                <Rate
                  value={rating}
                  onChange={handleRatingChange}
                  character={<StarOutlined />}
                  style={{ fontSize: '32px', color: '#fadb14' }}
                />
              </div>
              <div className="rating-labels">
                <Text className="rating-label-left">Very bad</Text>
                <Text className="rating-label-right">Very good</Text>
              </div>
            </div>
          </Card>
        )}

        {showDetailedFeedback && !isHost && (
          <Card className="detailed-feedback-card">
            <div className="feedback-section">
              <div className="left-panel">
                <Text className="feedback-title">
                  {getFeedbackOptions().title}
                  <span className="required-asterisk">*</span>
                </Text>

                <div className="feedback-options">
                  <Checkbox.Group
                    options={getFeedbackOptions().options.map((option) => ({
                      label: option,
                      value: option
                    }))}
                    value={selectedOptions}
                    onChange={handleOptionChange}
                  />
                </div>
              </div>

              <div className="right-panel">
                <div className="additional-feedback">
                  <Text className="additional-feedback-title">Additional feedback?</Text>
                  <TextArea
                    placeholder="Your Answer"
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    className="feedback-textarea"
                  />
                </div>

                <div className="submit-section">
                  <Button
                    type="primary"
                    size="large"
                    className="submit-button"
                    onClick={handleSubmitFeedback}
                    loading={isSubmitting}
                    disabled={selectedOptions.length === 0}
                  >
                    Submit
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        )}

        {showThankYou && (
          <Card className="thank-you-card">
            <div className="thank-you-content">
              <div className="thank-you-icon">
                <CheckCircleOutlined style={{ fontSize: '48px', color: '#52c41a' }} />
              </div>
              <div className="thank-you-text">
                <Text strong className="thank-you-title">
                  Thank you for submitting feedback
                </Text>
              </div>
            </div>
          </Card>
        )}

        {!showDetailedFeedback && !showThankYou && (
          <Card className="security-info">
            <div className="security-content">
              <div className="security-icon">
                <SecurityScanOutlined style={{ fontSize: '32px', color: '#7352d1' }} />
              </div>
              <div className="security-text">
                <Text strong className="security-title">
                  Your meeting is safe
                </Text>
                <Text className="security-description">No one can join a meeting unless invited</Text>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Leave;
